import type { ComponentPropsWithRef, ReactNode } from "react";

import { cn } from "../../lib/cn";

/**
 * Vocabulaire d'états d'une console cloud.
 *
 * ── Pourquoi une liste fermée plutôt qu'un ton libre ─────────────────────────
 * Dans une console, l'état d'une ressource est l'information la plus lue de
 * toute l'interface : c'est ce qu'on vient vérifier avant tout le reste. Laisser
 * chaque écran choisir sa couleur produit invariablement le même résultat —
 * « stopped » en rouge sur la page des instances, en gris sur celle des bases,
 * et l'opérateur perd la seule chose qu'un code couleur doit garantir : pouvoir
 * conclure sans lire.
 *
 * La liste distingue donc trois familles, et c'est la distinction qui compte :
 *
 *   • ce qui va bien       → `running`, `succeeded`
 *   • ce qui BOUGE         → `provisioning`, `pending`, `updating`, `deleting`
 *   • ce qui demande à moi → `degraded`, `failed`, `stopped`, `unknown`
 *
 * Un état transitoire n'est PAS un avertissement : une instance en cours de
 * création n'appelle aucune action, elle appelle de la patience. La peindre en
 * orange, c'est déclencher une astreinte pour un déploiement normal. Elle porte
 * donc la couleur de l'accent et une animation — le mouvement dit « en cours »
 * bien mieux qu'une couleur.
 */
export type CloudStatus =
  | "running"
  | "succeeded"
  | "provisioning"
  | "pending"
  | "updating"
  | "deleting"
  | "degraded"
  | "failed"
  | "stopped"
  | "unknown";

interface StatusDefinition {
  label: string;
  /** Couleur du point et du texte. */
  className: string;
  /** Fond de l'étiquette. */
  badgeClassName: string;
  /** L'état est transitoire : il se résoudra seul, sans action de l'opérateur. */
  transient: boolean;
}

export const CLOUD_STATUS: Record<CloudStatus, StatusDefinition> = {
  running: {
    label: "En service",
    className: "text-success-fg",
    badgeClassName: "bg-success-subtle text-success-fg",
    transient: false,
  },
  succeeded: {
    label: "Réussi",
    className: "text-success-fg",
    badgeClassName: "bg-success-subtle text-success-fg",
    transient: false,
  },
  provisioning: {
    label: "Provisionnement",
    className: "text-accent-fg",
    badgeClassName: "bg-accent-subtle text-accent-fg",
    transient: true,
  },
  pending: {
    label: "En attente",
    className: "text-accent-fg",
    badgeClassName: "bg-accent-subtle text-accent-fg",
    transient: true,
  },
  updating: {
    label: "Mise à jour",
    className: "text-accent-fg",
    badgeClassName: "bg-accent-subtle text-accent-fg",
    transient: true,
  },
  deleting: {
    label: "Suppression",
    className: "text-accent-fg",
    badgeClassName: "bg-accent-subtle text-accent-fg",
    transient: true,
  },
  degraded: {
    label: "Dégradé",
    className: "text-warning-fg",
    badgeClassName: "bg-warning-subtle text-warning-fg",
    transient: false,
  },
  failed: {
    label: "En échec",
    className: "text-danger-fg",
    badgeClassName: "bg-danger-subtle text-danger-fg",
    transient: false,
  },
  stopped: {
    label: "Arrêté",
    className: "text-fg-muted",
    badgeClassName: "bg-surface-sunken text-fg-muted ring-1 ring-border ring-inset",
    transient: false,
  },
  unknown: {
    label: "Inconnu",
    className: "text-fg-subtle",
    badgeClassName: "bg-surface-sunken text-fg-subtle ring-1 ring-border ring-inset",
    transient: false,
  },
};

export interface StatusDotProps extends ComponentPropsWithRef<"span"> {
  status: CloudStatus;
}

/**
 * Pastille d'état seule, pour une cellule de tableau ou un en-tête dense.
 *
 * Les états transitoires — et EUX SEULS — pulsent. Une console affiche des
 * centaines de pastilles : si toutes s'animent, l'animation ne veut plus rien
 * dire et la page consomme du GPU pour rien. Ici le mouvement est l'information.
 */
export function StatusDot({ status, className, ...props }: StatusDotProps) {
  const def = CLOUD_STATUS[status];

  return (
    <span
      className={cn("relative flex size-2 shrink-0", def.className, className)}
      aria-hidden="true"
      {...props}
    >
      {def.transient && (
        <span
          className="absolute inline-flex size-full animate-ping rounded-full bg-current opacity-60"
          data-motion-essential=""
        />
      )}
      <span className="relative inline-flex size-full rounded-full bg-current" />
    </span>
  );
}

export interface StatusBadgeProps extends ComponentPropsWithRef<"span"> {
  status: CloudStatus;
  /** Remplace le libellé par défaut (« En service », « Dégradé »…). */
  label?: ReactNode;
}

/**
 * État nommé.
 *
 * La couleur n'est jamais seule porteuse du sens : le libellé est toujours là.
 * C'est WCAG 1.4.1, et c'est surtout la seule façon qu'un daltonien
 * deutéranope distingue « en service » de « en échec » — deux états dont les
 * couleurs sont, pour lui, identiques.
 */
export function StatusBadge({ status, label, className, ...props }: StatusBadgeProps) {
  const def = CLOUD_STATUS[status];

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        def.badgeClassName,
        className,
      )}
      {...props}
    >
      <StatusDot status={status} className="size-1.5" />
      {label ?? def.label}
    </span>
  );
}
